import { PromiseCompletionSource } from "scene-manager";
import { ICanvasScaler } from "../../../canvas/ICanvasScaler";
import { AdaptiveView } from "./AdaptiveView";
import { LanguagesBox } from "../components/LanguagesBox";
import { FullScreenFade } from "../components/FullScreenFade";
import { Toggle } from "../components/Toggle";
import { IDestroyOptions, Sprite, Texture } from "pixi.js";
import { CommonResourcesPackage } from "../resources/CommonResourcesPackage";
import { SettingsModel } from "../models/SettingsModel";
import { SettingsController } from "../controllers/SettingsController";
import { Language } from "../../localization/Localization";
import { UIButtonBase } from "../components/UIButtonBase";


export abstract class BaseSettingsView<TResult> extends AdaptiveView<TResult> {
    protected fade: FullScreenFade;
    protected closeButton: UIButtonBase;
    protected musicToggle: Toggle;
    protected soundToggle: Toggle;
    protected languagesBox: LanguagesBox;

    public constructor(
        protected readonly commonResources: CommonResourcesPackage,
        protected readonly settingsModel: SettingsModel,
        protected readonly settingsController: SettingsController,
        protected readonly frameTexture: Texture,
        canvasScaler: ICanvasScaler,
        operationControls: PromiseCompletionSource<TResult>
    ) {
        super(canvasScaler, operationControls);
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight, true);

        this.musicToggle.onChange.add(this.onMusicStateChange, this);
        this.soundToggle.onChange.add(this.onSoundStateChange, this);
        this.languagesBox.onLanguageClick.add(this.onLanguageClick, this);
        this.cursor = "hover";
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        this.musicToggle.onChange.remove(this.onMusicStateChange, this);
        this.soundToggle.onChange.remove(this.onSoundStateChange, this);
        this.languagesBox.onLanguageClick.remove(this.onLanguageClick, this);
        super.destroy(options);
    }

    protected build(): void {
        this.fade = new FullScreenFade(this.canvasScaler, 0.8);
        this.frame = new Sprite(this.frameTexture);
        this.closeButton = new UIButtonBase({ defaultTexture: this.commonResources.assets_common_close_button_png });

        this.musicToggle = new Toggle(
            this.commonResources.assets_common_button_background_png,
            this.commonResources.assets_common_music_on_png,
            this.commonResources.assets_common_music_off_png,
            this.settingsModel.musicEnabled.value
        );
        this.musicToggle.icon.position.set(52, 37);

        this.soundToggle = new Toggle(
            this.commonResources.assets_common_button_background_png,
            this.commonResources.assets_common_sound_on_png,
            this.commonResources.assets_common_sound_off_png,
            this.settingsModel.sfxEnabled.value
        );
        this.soundToggle.icon.position.set(51, 46);

        this.languagesBox = new LanguagesBox(this.commonResources, this.settingsModel);

        this.frame.addChild(this.closeButton, this.musicToggle, this.soundToggle, this.languagesBox);
        this.addChild(this.fade, this.frame);
    }

    private onLanguageClick(language: Language): void {
        this.settingsController.changeLocalization(language);
    }

    private onMusicStateChange(value: boolean): void {
        this.settingsController.setMusicEnabled(value);
    }

    private onSoundStateChange(value: boolean): void {
        this.settingsController.setSfxEnabled(value);
    }
}
